import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { ListingReviewEntity, ListingsFacade, UpdateListingReviewsFormEntity } from '@app/listings';
import { PropertiesFacade, PropertyEntity } from '@app/properties';
import { NoteType } from '@app/shared/components/note/note.interface';
import {
  ChannelListing,
  combineLoadingStates,
  ListingReview,
  LoadingState,
  Property,
  UpdatePropertyOption,
} from '@app/shared/interfaces';
import { Channel } from '@app/shared/models/channel';
import { DialogPropertyMutingService } from '@app/shared/services/property-muting/dialog-property-muting.service';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

enum ReviewListingsStage {
  ReviewListings = 'ReviewListings',
  ConfirmListings = 'ConfirmListings',
}

@Component({
  standalone: false,
  selector: 'sbnb-review-listings',
  templateUrl: './review-listings.component.html',
  styleUrls: ['./review-listings.component.scss'],
})
export class ReviewListingsComponent implements OnInit, OnDestroy {
  platform: Channel;
  channelId: string;
  perms$ = this.userPermissionsService;

  tabNames = {
    hosted: 'Hosted listings',
    cohosted: '⚠️ Co-hosted listings',
    archived: '⚠️ Archived listings',
  };

  pageLoadingStateSubscription$: Subscription;
  pageLoadingState$: Observable<LoadingState>;

  listingReviews$: Observable<ListingReview | null>;
  listingReviewsListingEntities$: Observable<ListingReviewEntity['listings']>;
  listingReviewsHasCohosted$: Observable<boolean>;
  updateListingReviewsFormEntity$: Observable<UpdateListingReviewsFormEntity>;
  updateListingReviewsCombinedLoadingStates$: Observable<LoadingState>;

  listingReviewsToBeMerged$: Observable<any>;
  listingReviewsToBeMuted$: Observable<any>;
  listingReviewsToBeUnMuted$: Observable<any>;
  propertiesToBeCreated$: Observable<any>;

  propertyEntities$: Observable<PropertyEntity>;
  properties$: Observable<Property[]>;
  propertiesMergeCandidatesSubscription$: Subscription;

  LoadingState = LoadingState;
  NoteType = NoteType;
  UpdatePropertyOption = UpdatePropertyOption;
  reviewListingsStage = ReviewListingsStage.ReviewListings;
  ReviewListingsStage = ReviewListingsStage;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly pusher: PusherService,
    private readonly dialogPropertyMutingService: DialogPropertyMutingService,
    private readonly userPermissionsService: UserPermissionsService,
    private readonly router: Router,

    private propertiesFacade: PropertiesFacade,
    private listingsFacade: ListingsFacade,
    private segmentIoService: SegmentIoService
  ) {}

  ngOnInit(): void {
    this.platform = this.route.snapshot.paramMap.get('platform') as Channel;
    this.channelId = this.route.snapshot.paramMap.get('channel_id');

    this.listingReviews$ = this.listingsFacade.listingReviews$;
    this.listingReviewsListingEntities$ = this.listingsFacade.listingReviewsListingEntities$;
    this.listingReviewsHasCohosted$ = this.listingsFacade.listingReviewsHasCohosted$;
    this.updateListingReviewsFormEntity$ = this.listingsFacade.updateListingReviewsFormEntity$;

    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.propertiesMergeCandidatesSubscription$ && this.propertiesMergeCandidatesSubscription$.unsubscribe) {
      this.propertiesMergeCandidatesSubscription$.unsubscribe();
    }

    if (this.pageLoadingStateSubscription$ && this.pageLoadingStateSubscription$.unsubscribe) {
      this.pageLoadingStateSubscription$.unsubscribe();
    }
  }

  loadData() {
    this.listingReviewsToBeMerged$ = this.listingsFacade.listingReviewsFormByAction$(
      UpdatePropertyOption.MergeProperty
    );
    this.listingReviewsToBeMuted$ = this.listingsFacade.listingReviewsFormByAction$(UpdatePropertyOption.MuteProperty);
    this.listingReviewsToBeUnMuted$ = this.listingsFacade.listingReviewsFormByAction$(
      UpdatePropertyOption.UnmuteProperty
    );
    this.propertiesToBeCreated$ = this.listingsFacade.listingReviewsFormByAction$(
      UpdatePropertyOption.CreateNewProperty
    );
    this.updateListingReviewsCombinedLoadingStates$ = this.listingsFacade.updateListingReviewsCombinedLoadingStates$;
    this.listingsFacade.loadListingReviews(this.platform, this.channelId, true);
    this.propertiesFacade.loadProperties(false);
    this.properties$ = this.propertiesFacade.properties$;
    this.propertyEntities$ = this.propertiesFacade.propertyEntities$;
    this.propertiesMergeCandidatesSubscription$ = this.propertiesFacade.propertiesMergeCandidateEntities$.subscribe(
      (propertyMergeCandidateEntities) => {
        if (propertyMergeCandidateEntities) {
          const form: UpdateListingReviewsFormEntity = {};

          Object.entries(propertyMergeCandidateEntities).forEach((entity) => {
            const [key, value] = entity;
            form[+key] = {
              propertyId: key,
              listingId: value.listingId,
              action: value.merge_candidate
                ? ((UpdatePropertyOption.MergeProperty + value.merge_candidate) as UpdatePropertyOption)
                : UpdatePropertyOption.CreateNewProperty,
              loadingState: LoadingState.NotSent,
            };
          });

          this.listingsFacade.updateListingReviewsFormEntity(form);
        }
      }
    );

    this.pageLoadingState$ = combineLatest(
      this.listingsFacade.listingReviewsLoadingState$,
      this.propertiesFacade.propertiesLoadingState$,
      this.propertiesFacade.propertiesMergeCandidatesLoadingState$
    ).pipe(
      map(([listingsLoadingState, propertiesLoadingState, propertiesMergeCandidatesLoadingState]) =>
        combineLoadingStates([listingsLoadingState, propertiesLoadingState], [propertiesMergeCandidatesLoadingState])
      )
    );

    this.pageLoadingStateSubscription$ = this.pageLoadingState$.subscribe((loadingState) => {
      if (loadingState === LoadingState.Success) {
        this.listingsFacade.trackStartedMergeMatch();
      }

      if (loadingState === LoadingState.Error) {
        this.segmentIoService.track(SegmentEvent.MergeMatchError, {});
      }
    });

    this.listenForPusherListingUpdates(true);
  }

  listenForPusherListingUpdates(shouldLoadMergeCandidates = false) {
    this.pusher.listingsUpdate.subscribe((res) => {
      if (res.platform_id == this.channelId && res.listings) {
        this.listingsFacade.listingReviewsPusherUpdate(res.listings, shouldLoadMergeCandidates);
      }
    });
  }

  updateForm(listingId: string, propertyId: string, option: UpdatePropertyOption) {
    this.listingsFacade.updateListingReviewsFormEntity({
      [propertyId]: {
        propertyId,
        listingId,
        action: option,
        loadingState: LoadingState.NotSent,
      },
    });
  }

  muteDialog(mode: 'mute' | 'unmute', listing: ChannelListing) {
    // todo
    this.dialogPropertyMutingService.openMutingDialog(mode, listing.property_id).subscribe((res) => {
      if (res && 'muted' in res) {
        listing.muted = res.muted;
      }
    });
  }

  continue(hasCohosted) {
    if (this.reviewListingsStage === ReviewListingsStage.ReviewListings) {
      this.reviewListingsStage = ReviewListingsStage.ConfirmListings;
    }
  }

  save(hasCohosted) {
    if (this.reviewListingsStage === ReviewListingsStage.ConfirmListings) {
      this.listingsFacade.saveListingReviewsForm();

      this.updateListingReviewsCombinedLoadingStates$.subscribe((loadingState) => {
        if (loadingState === LoadingState.Success) {
          if (hasCohosted) {
            this.router.navigate([`/accounts/new/invite-your-hosts/${this.channelId}`], {
              queryParams: { platform: this.platform },
            });
          } else {
            this.router.navigate(['/accounts/new/complete'], {
              queryParams: { platform: this.platform },
            });
          }
        }
      });
    }
  }
}
